import React, {useMemo} from 'react'
import ContentSection from './ContentSection'
import {FlexButtonBar} from './buttons'
import {ActionButtonIcon, CancelButton, DeleteButton, SaveButton} from './SaveSubmitButton'
import ContentIndent from './ContentIndent'
import ButtonRow from './ButtonRow'
import {GridCol, GridRow, GridRowSingleCol, GridSection, HelpText} from './grid-form'
import AioText from './aio-inputs/AioText'
import AioSelect from './aio-inputs/AioSelect'
import AioTextArea from './aio-inputs/AioTextArea'
import {useMergeExistingReducer, useMergeReducer} from '../hooks/useMergeReducer'
import {postJson, resolveRoute, responseToError} from '../helpers/ajax'
import type {OptionTuple, OptionTuples} from '../helpers/react-helpers'
import _ from 'lodash'
import type {BlockedDateBoxState, BlockedDateChangeEvent} from './BlockedDateBox';
import BlockedDateBox, { DEFAULT_BLOCKED_DATE_STATE} from './BlockedDateBox'
import type {Store} from '../helpers/react-form';
import { useStore} from '../helpers/react-form'
import {AioColorPicker} from './aio-inputs/AioColorPicker'
import * as kb from '../kb'
import {useMountEffect} from '../hooks/useMountEffect'
import useEvent from '../hooks/useEvent'
import AioDollar from './aio-inputs/AioDollar'
import icons from '../../markons/markons'
import {MultiSlider} from './Slider'
import AioCheckbox from './aio-inputs/AioCheckbox'
import AioCheckboxList from './aio-inputs/AioCheckboxList'
import Icon from './Icon'
import ActionLink from './ActionLink'
import AioNumber from './aio-inputs/AioNumber'
import {useSubmitRedirectHandler} from '../helpers/react-hooks'
import css from './VehicleForm.less'
import ActionButton from './ActionButton'
import type {ImageUpload, UploadChangeEventHandler, UploadStatusUpdateEventHandler} from './ImageUploadForm';
import { ImageUploadForm} from './ImageUploadForm'
import LoadingDots from './LoadingDots'
import {InternalLink} from './external-links'
import {arrayDeleteIndex, arrayReplace, arraySplice} from '../util/collections'
import type { RoundMode} from '../util/math';
import {floatFix, parseFloat, roundNearest} from '../util/math'
import type {CheckboxOption} from '../types/shared-input-types'


interface RateRange {
    max: number
    min: number
    minDistance: number
    step: number
}

type VehicleFormProps = {
    vehicleId: string | number | null
    isSuper: boolean
    createDefault?: boolean
    rateRange: RateRange
    /** @deprecated */
    onCancel?: VoidFn
    /** @deprecated */
    afterSave?: VoidFn
    gnetEnabled: boolean
    gnetVehicleOptions: OptionTuples,
}


interface GetVehicleResponse {
    distanceUnit: string;
    colors: Color[];
    vehicle: Vehicle;
    features: CheckboxOption[];
    companies: Company[];
    vbdReasons: OptionTuples;
    currencySymbol: string;
    currencyCode: string;
    distanceRounding: RoundMode;
    distanceNearest: StringNum;
    images: ImageUpload[];
}

type ServerOptions = Omit<GetVehicleResponse, 'vehicle'>

type GetVehicleRequest = {
    id: string | number
    default?: boolean
}

interface Color {
    int: number;
    name: string;
    style: Style;
}

interface Style {
    backgroundColor: string;
    color: string;
}

interface Company {
    name: string;
    id: number;
}

interface Vehicle {
    companyId: string;
    name: string;
    internalName: string;
    unitNbr: string;
    color: string;
    capacity: string;
    description: string;
    minRate: string;
    maxRate: string;
    defaultRate: string;
    distanceRates: DistanceRate[];
    featureIds: StringNum[];
    imageIds: number[];
    distanceBasePrice: string;
    isAffiliate: boolean;
    enabledOnQuoteForm: boolean;
    blockedDates: any[];
    excludeCustomerSurvey: boolean;
}

type DistanceRate = {
    upTo: number | null;
    rate: string;
}

interface SectionProps {
    store: Store<FormData>
    options: AllOptions
}

function VehicleSection({store, options}: SectionProps) {
    return (
        <ContentSection title="Basic Details">
            <GridSection>
                <GridRow>
                    <GridCol minWidth={200}>
                        <AioText label="Name" {...store.input('name')}/>
                    </GridCol>
                    <GridCol minWidth={120}>
                        <AioSelect label="Capacity" icon={icons.faUserFriends} placeholder="— Capacity —"
                                   options={_.range(1, 151)} {...store.input('capacity')}/>
                    </GridCol>
                </GridRow>
                <GridRowSingleCol>
                    <AioTextArea label="Description" {...store.input('description')}/>
                </GridRowSingleCol>
                <GridRowSingleCol>
                    <AioCheckboxList label="Features" values={store.data.featureIds}
                                     onChange={ev => store.set('featureIds', ev.values)} options={options.features} noItems={<>No features available. Please <InternalLink route={"features.create"}>create some</InternalLink>.</>}/>
                </GridRowSingleCol>
                <GridRow>
                    <GridCol minWidth={120}>
                        <AioText label="License Plate #" {...store.input('licensePlate')}/>
                    </GridCol>
                </GridRow>
            </GridSection>
        </ContentSection>
    )
}

function VehicleOptionsSection({store, options}: SectionProps) {
    const presetColors = useMemo(() => options.colors.map(c => c.style.backgroundColor), [options.colors])
    const unitNbr = (store.get('unitNbr') || 'UNIT#').slice(0, 6).toUpperCase()
    const bgColor = store.get('bgColor') || '#ccc'

    const gnetVehicleTypes = useMemo(() => options.gnetVehicleOptions.map(([value,label]) => ({
        label,
        value,
    })), [options.gnetVehicleOptions])


    return (
        <ContentSection title="Internal Options">
            <GridSection>
                <GridRow>
                    <GridCol minWidth={120}>
                        <AioText label="Unit # ID" {...store.input('unitNbr')}/>
                        <HelpText>Short vehicle ID (1-6 chars). Leave blank to assign randomly.</HelpText>
                    </GridCol>
                    <GridCol minWidth={120}>
                        <AioColorPicker label="Unit # Color" disableAlpha color={bgColor}
                                        onChange={c => store.set('bgColor', c.hex)} presetColors={presetColors}
                                        text={unitNbr}/>
                    </GridCol>
                </GridRow>
                <GridRowSingleCol>
                    <AioText label="Internal Name" {...store.input('internalName')}/>
                    <HelpText>Leave blank to use the public name.</HelpText>
                </GridRowSingleCol>
                {options.gnetEnabled ? <GridRowSingleCol>
                    <AioCheckboxList label="GNet Vehicle Type(s)"
                        options={gnetVehicleTypes} {...store.checkboxList('gnetVehicleTypes')}/>
                    <HelpText>When you receive a booking from GNet, this vehicle will be picked when the sender chooses one of these vehicle types. Do not choose types that overlap with another one of your vehicles.</HelpText>
                </GridRowSingleCol> : null}
                <GridRowSingleCol>
                    <AioCheckbox label="Affiliate's vehicle" {...store.checkbox('isAffiliate')}/>
                    <HelpText>Add internal note through-out the system that this
                        particular vehicle belongs to an affiliate.</HelpText>
                </GridRowSingleCol>
                <GridRowSingleCol>
                    <AioCheckbox label="Show on quote form" {...store.checkbox('enabledOnQuoteForm')}/>
                    <HelpText>List this vehicle as a possible option on the Quote
                        Request Form.</HelpText>
                </GridRowSingleCol>
                <GridRowSingleCol>
                    <AioCheckbox
                        label="Exclude from receiving follow-up email" {...store.checkbox('excludeCustomerSurvey')}/>
                    <HelpText>The follow-up email will not be sent for bookings with this as one of the scheduled
                        vehicles.</HelpText>
                </GridRowSingleCol>
            </GridSection>
        </ContentSection>
    )
}


const MIN_HOURS: OptionTuple[] = [
    [0, "None (No Minimum)"],
    [15, "15 minutes"],
    [30, "30 minutes"],
    [45, "45 minutes"],
    [60, "1 hour"],
    [60 + 15, "1 hour, 15 minutes"],
    [60 + 30, "1 hour, 30 minutes"],
    [60 + 45, "1 hour, 45 minutes"],
    [2 * 60, "2 hours"],
    [2.5 * 60, "2.5 hours"],
    [3 * 60, "3 hours"],
    [3.5 * 60, "3.5 hours"],
    [4 * 60, "4 hours"],
    [4.5 * 60, "4.5 hours"],
    [5 * 60, "5 hours"],
    [5.5 * 60, "5.5 hours"],
    [6 * 60, "6 hours"],
    [7 * 60, "7 hours"],
    [8 * 60, "8 hours"],
]


function HourlyPricingSection({store, options}: SectionProps) {
    return (
        <ContentSection title="Hourly Pricing">
            <GridSection>
                <GridRowSingleCol>
                    <AioSelect label="Minimum Billed Hours" options={MIN_HOURS} {...store.input('minMinutes')}/>
                    <HelpText>Applies to single-vehicle bookings only.</HelpText>
                </GridRowSingleCol>
                <GridRow>
                    <GridCol minWidth={150}>
                        <AioDollar label="Minimum Rate" {...store.input('minRate')}/>
                    </GridCol>
                    <GridCol minWidth={150}>
                        <AioDollar label="Default Rate" {...store.input('defaultRate')}/>
                    </GridCol>
                    <GridCol minWidth={150}>
                        <AioDollar label="Maximum Rate" {...store.input('maxRate')}/>
                    </GridCol>
                </GridRow>
                <GridRowSingleCol>
                    <MultiSlider values={[store.data.minRate, store.data.defaultRate, store.data.maxRate]}
                                 min={options.rateRange.min} max={options.rateRange.max}
                                 minDistance={options.rateRange.minDistance} step={options.rateRange.step}
                                 onChange={({handleIndex, values}) => {
                                     store.shallowMerge({
                                         minRate: values[0],
                                         defaultRate: values[1],
                                         maxRate: values[2],
                                     })
                                 }}/>
                    <HelpText>Customers will be billed at the <b>Default Rate</b> unless you adjust it by editing the quote.</HelpText>
                </GridRowSingleCol>
            </GridSection>
        </ContentSection>
    )
}

const distMultiplier = 1.5
const rateMultiplier = 0.9

function DistanceBasedPricingSection({store, options}: SectionProps) {
    const distanceRates = store.data.distanceRates ?? []
    const distanceBasePrice = parseFloat(store.data.distanceBasePrice, 0)
    let runningPrice = distanceBasePrice
    let lastUpTo = 0
    let lastRate = Number.POSITIVE_INFINITY
    const baseDist = options.distanceUnit === 'km' ? 40 : 25

    const roundDist = (dist: number) => roundNearest(dist, options.distanceNearest, options.distanceRounding)

    const addDistanceTier = useEvent(() => {
        store.set('distanceRates', rates => {
            if(rates.length >= 1 && !rates[rates.length - 1].upTo) {
                rates = [...rates]
                rates[rates.length - 1] = {
                    ...rates[rates.length - 1],
                    upTo: roundDist(parseFloat(rates[rates.length - 2]?.upTo as any, 0) * distMultiplier || baseDist),
                }
            }
            return [...rates, {
                upTo: null,
                rate: (parseFloat(rates[rates.length - 1]?.rate, 0) * rateMultiplier || (distanceBasePrice || 100) / baseDist * 5).toFixed(2),
            }]
        })
    })

    return (
        <ContentSection title="Distance-Based Pricing">
            <AioDollar label="Base Price" {...store.input('distanceBasePrice')} inline/> <Icon name={icons.miscPlus}/>
            {distanceRates.length ?
                <table className="simple-table" style={{marginTop: '8px', width: 'auto'}}>
                    <thead>
                        <tr>
                            <th style={{width: '150px'}}>Distance ({options.distanceUnit})</th>
                            <th style={{width: '150px'}}>Rate</th>
                            <th style={{width: '170px'}}>Example</th>
                            <th style={{width: 1}} className="actions"/>
                        </tr>
                    </thead>
                    <tbody>
                        {distanceRates.map((dist, i) => {
                            const lowerBound = i > 0 ? parseFloat(distanceRates[i - 1].upTo!, 0) : 0
                            const isLast = i >= distanceRates.length - 1
                            const upTo = parseFloat(store.get(['distanceRates', i, 'upTo']), 0)
                            const exampleDist = roundDist(isLast ? (lowerBound > 0 ? lowerBound * distMultiplier : baseDist) : upTo)
                            const segDist = exampleDist - lowerBound
                            const segRate = parseFloat(store.get(['distanceRates', i, 'rate']), 0)
                            runningPrice += segDist * segRate

                            const deleteDistanceTier = () => {
                                store.set('distanceRates', distanceRates => arraySplice(distanceRates, i))
                            }

                            // error={!!segRate && segRate > lastRate}
                            const render = (
                                <tr key={i}>
                                    <td className="vcenter">
                                        {!isLast ?
                                            <><AioNumber icon={icons.faRoute} label="Up to" step="0.01" min="0.00"
                                                         max="9999999.99" {...store.input(['distanceRates', i, 'upTo'])}
                                                         error={!!upTo && upTo < lastUpTo}/></>
                                            : (distanceRates.length === 1 ? "Any" : <>Over {lowerBound} {options.distanceUnit}</>)}
                                    </td>
                                    <td className="vcenter">
                                        <AioDollar label={`Price per ${options.distanceUnit}`}
                                                   step="0.01"
                                                   min="0.00"
                                                   max="9999999.99" {...store.input(['distanceRates', i, 'rate'])}/>
                                    </td>
                                    <td className="vcenter">
                                        {floatFix(exampleDist)} {options.distanceUnit} = {kb.formatCurrencyIntl(runningPrice, options.currencyCode)}
                                    </td>
                                    <td className="vcenter actions">
                                        <ActionLink onClick={deleteDistanceTier}
                                                    className="mrkn ion-trash-a delete-link"/>
                                    </td>
                                </tr>
                            )

                            lastUpTo = upTo
                            lastRate = segRate

                            return render
                        })}
                    </tbody>
                </table> : null}

            <FlexButtonBar marginTop={8}>
                <ActionButtonIcon icon={icons.miscPlus} className="btn-new" onClick={addDistanceTier}>
                    Add Tier
                </ActionButtonIcon>
            </FlexButtonBar>
        </ContentSection>
    )
}

function BlockedDatesSection({store, options}: SectionProps) {
    const blockedDates = store.data.blockedDates ?? []

    const deleteBlockedDate = ((idx: number) => (ev: BlockedDateChangeEvent) => {
        store.set('blockedDates', bd => arrayDeleteIndex(bd, idx))
        if(ev.value.id) {
            store.push('deletedBlockedDates', ev.value.id)
        }
    })

    const handleChange = ((idx: number) => (ev: BlockedDateChangeEvent) => {
        store.set('blockedDates', bd => arrayReplace(bd, idx, ev.value))
    })

    const addDate = (() => {
        store.push('blockedDates', DEFAULT_BLOCKED_DATE_STATE)
    })

    return (
        <ContentSection title="Blocked Dates">
            {blockedDates?.length
                ? <div className={css.dateGrid}>
                    {blockedDates.map((bd, idx) => (
                        <BlockedDateBox key={idx}
                                        defaultValue={bd}
                                        onChange={handleChange(idx)}
                                        onDelete={deleteBlockedDate(idx)}
                                        reasons={options.vbdReasons}/>)
                    )}
                </div>
                : <p>No blocked dates.</p>
            }
            <ButtonRow>
                <ButtonRow.Left>
                    <ActionButton className="btn btn-new" onClick={addDate}>
                        <Icon name={icons.ionAndroidAddCircle}/> Add Blocked Date Range
                    </ActionButton>
                </ButtonRow.Left>
            </ButtonRow>
        </ContentSection>
    )
}

type PhotoSectionProps = SectionProps & {
    onStatusUpdate: UploadStatusUpdateEventHandler
}

function PhotoSection({store, options, onStatusUpdate}: PhotoSectionProps) {
    const changeHandler = useEvent<UploadChangeEventHandler>(ev => {
        store.set('imageIds', ev.imageIds)
    })

    return (
        <ContentSection title="Photos">
            <ImageUploadForm images={options.images} onStatusUpdate={onStatusUpdate} onChange={changeHandler} initialAspectRatio={7/5} forceAspectRatio={true}/>
        </ContentSection>
    )
}


type AllOptions = ServerOptions & VehicleFormProps


const DEFAULT_SERVER_OPTIONS: ServerOptions = {
    distanceUnit: "km",
    colors: [],
    features: [],
    companies: [],
    vbdReasons: [],
    currencySymbol: "$",
    currencyCode: "USD",
    distanceRounding: 'NEAREST',
    distanceNearest: 5,
    images: [],
}

type FormData = {
    companyId: StringNum | null;
    name: string;
    internalName: string;
    gnetVehicleTypes: string[];
    unitNbr: string;
    /** @deprecated */
    color: StringNum;
    bgColor: string
    capacity: string;
    description: string;
    licensePlate: string;
    minMinutes: string | number;
    minRate: string | number;
    maxRate: string | number;
    defaultRate: string | number;
    distanceRates: DistanceRate[];
    featureIds: StringNum[];
    distanceBasePrice: string;
    isAffiliate: boolean;
    enabledOnQuoteForm: boolean;
    blockedDates: BlockedDateBoxState[];
    deletedBlockedDates: number[];
    excludeCustomerSurvey: boolean;
    id: StringNum | null;
    imageIds: number[]
}

const DEFAULT_FORM_DATA: FormData = {
    id: null,
    companyId: window.user?.companyId ?? null,
    name: '',
    internalName: '',
    gnetVehicleTypes: [],
    unitNbr: '',
    color: '',
    bgColor: '',
    capacity: '',
    description: '',
    licensePlate: '',
    minMinutes: 0,
    minRate: '',
    maxRate: '',
    defaultRate: '',
    distanceRates: [],
    featureIds: [],
    distanceBasePrice: '',
    isAffiliate: false,
    enabledOnQuoteForm: true,
    blockedDates: [],
    deletedBlockedDates: [],
    excludeCustomerSurvey: false,
    imageIds: [],
}

type LoadingState = {
    loading: boolean
    error: Error|null
    activeUploads: number,
}

export default function VehicleForm(props: VehicleFormProps) {
    const {vehicleId, createDefault, onCancel} = props
    const store = useStore(DEFAULT_FORM_DATA)
    const [state,dispatch] = useMergeReducer<LoadingState>({
        loading: true,
        error: null,
        activeUploads: 0,
    })
    const [options, mergeOptions] = useMergeExistingReducer<AllOptions>({...DEFAULT_SERVER_OPTIONS, ...props})

    const discardChanges = useEvent(() => {
        if(onCancel) {
            onCancel()
        } else {
            kb.navigate(createDefault ? resolveRoute('sudo.defaultVehicles') : resolveRoute('vehicles.index'))
        }
    })

    useMountEffect(() => {
        postJson<GetVehicleResponse, GetVehicleRequest>(resolveRoute('getVehicle'), {
            id: vehicleId,
            default: !!createDefault
        }).then(res => {
            if(res.ok) {
                const {vehicle, ...options} = res.data
                const formData: Partial<FormData> = {id: vehicleId, ...vehicle}
                if(!formData?.distanceRates?.length) {
                    formData.distanceRates = [{rate: '', upTo: null}]
                }
                formData.deletedBlockedDates = []
                store.shallowMerge(formData)
                mergeOptions(options)
            } else {
                dispatch({error: responseToError(res)})
            }
        }, err => {
            dispatch({error: err})
        }).finally(() => {
            dispatch({loading: false})
        })
    })

    const [handleSave, isSaving] = useSubmitRedirectHandler(resolveRoute('vehicles.store'), {
        ...store.data,
        default: !!createDefault,
        id: vehicleId
    })

    const [handleDelete, isDeleting] = useSubmitRedirectHandler(resolveRoute('vehicles.store'), {
        action: 'DELETE',
        id: vehicleId
    })

    const uploadStatusHandler = useEvent<UploadStatusUpdateEventHandler>(({activeUploads}) => {
        dispatch({activeUploads})
    })

    if(state.loading) {
        return <LoadingDots height={300}/>
    }

    return <>
        <VehicleSection store={store} options={options}/>
        <VehicleOptionsSection store={store} options={options}/>
        <HourlyPricingSection store={store} options={options}/>
        <DistanceBasedPricingSection store={store} options={options}/>
        <BlockedDatesSection store={store} options={options}/>
        <PhotoSection store={store} options={options} onStatusUpdate={uploadStatusHandler}/>

        <ContentIndent>
            <ButtonRow>
                <ButtonRow.Left>
                    <SaveButton onClick={handleSave} disabled={state.activeUploads > 0} processing={isSaving}>Save</SaveButton>
                    {state.activeUploads ? <span>Please wait for {state.activeUploads} upload(s)</span> : null}
                </ButtonRow.Left>
                <ButtonRow.Right>
                    <CancelButton onClick={discardChanges}>Discard Changes</CancelButton>
                    <DeleteButton onClick={handleDelete} disabled={isDeleting}>Delete</DeleteButton>
                </ButtonRow.Right>
            </ButtonRow>
        </ContentIndent>
    </>
}
